import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store';
import { setShowHelp } from '../store/slices/preferencesSlice';
import { Settings, HelpCircle, CheckCircle, XCircle } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';

export function PreferencesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showHelp = useSelector((state: RootState) => state.preferences.showHelp);
  const [saving, setSaving] = useState(false);

  const handleToggleHelp = async () => {
    setSaving(true);
    // Simuler un petit délai pour le feedback visuel
    setTimeout(() => {
      dispatch(setShowHelp(!showHelp));
      setSaving(false);
    }, 200);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-3">
          <Settings className="w-8 h-8 text-primary-600" />
          <span>{t('settings.preferences.title')}</span>
          <HelpIcon 
            docPath="16-parametres#16-2-1-préférences" 
            tooltip="Documentation : Préférences"
          />
        </h1>
        <p className="text-textSecondary mt-2">
          {t('settings.preferences.subtitle')}
        </p>
      </div>

      {/* Preferences Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Help Display Preference */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <HelpCircle className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.preferences.helpDisplay.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.preferences.helpDisplay.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {showHelp ? t('settings.preferences.helpDisplay.visible') : t('settings.preferences.helpDisplay.hidden')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {showHelp 
                    ? t('settings.preferences.helpDisplay.visibleDescription')
                    : t('settings.preferences.helpDisplay.hiddenDescription')}
                </p>
              </div>
              <button
                onClick={handleToggleHelp}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  showHelp ? 'bg-primary-600' : 'bg-gray-200'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={showHelp}
                aria-label="Toggle help display"
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    showHelp ? 'translate-x-5' : 'translate-x-0'
                  }`}
                >
                  {showHelp ? (
                    <CheckCircle className="h-5 w-5 text-primary-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-400" />
                  )}
                </span>
              </button>
            </div>
          </div>

          {showHelp && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                {t('settings.preferences.helpDisplay.info')}
              </p>
            </div>
          )}
        </div>

        {/* Future preferences can be added here */}
      </div>

      {/* Info Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          {t('settings.preferences.about.title')}
        </h3>
        <p className="text-sm text-gray-600">
          {t('settings.preferences.about.description')}
        </p>
      </div>
    </div>
  );
}

